import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import { cleanGoogleMapsEmbedUrl } from '../../../utils/mapUtils';
import { 
  MapPin, 
  MessageSquare, 
  Clock, 
  Save, 
  Globe,
  Phone
} from 'lucide-react';
import './FormsUnified.css';

export default function ContactInfoForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    form_kicker: 'Contact Form',
    form_title: 'Send us a message',
    form_description: 'Share your questions and our team will respond as soon as possible.',
    whatsapp_number: '08815130338',
    location_kicker: 'Get In Touch',
    location_title: 'Visit Us Today',
    location_description: 'Located in the heart of Jakarta with easy access and a welcoming environment for families.',
    map_embed_url: 'https://www.google.com/maps?q=Permata%20Harapan%20School%20Jakarta&output=embed',
    address: 'Jl. Raya Permata Harapan No. 10, Jakarta Timur 13220',
    map_directions_url: 'https://maps.google.com/?q=Permata%20Harapan%20School%20Jakarta',
    phone: '+62 21 555 1234',
    email: 'info@permataharapan.sch.id',
    office_hours_weekday: 'Mon - Fri: 07:30 - 16:00',
    office_hours_weekend: 'Sat: 08:00 - 12:00',
    social_instagram: 'https://instagram.com/permataharapan',
    social_facebook: 'https://facebook.com/permataharapan',
    social_youtube: 'https://youtube.com/@permataharapan',
    social_linkedin: 'https://linkedin.com/school/permataharapan'
  });

  const [pendingFields, setPendingFields] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});

  const getAuthHeaders = () => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  useEffect(() => {
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadSection = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=contact-us`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'contact-info' || s.section_type === 'contact-location');
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=contact-us`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => s.section_type === 'contact-info' || s.section_type === 'contact-location');
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);
        const rawContent = foundSection.content_published || foundSection.content;
        let parsed = rawContent;
        if (typeof rawContent === 'string') {
          try {
            parsed = JSON.parse(rawContent);
          } catch (e) {
            console.error('Failed to parse contact-info content', e);
          }
        }

        const dataEn = parsed?.en || parsed || {};
        const newFormData = {
          form_kicker: dataEn.form_kicker || 'Contact Form',
          form_title: dataEn.form_title || 'Send us a message',
          form_description: dataEn.form_description || 'Share your questions and our team will respond as soon as possible.',
          whatsapp_number: dataEn.whatsapp_number || '08815130338',
          location_kicker: dataEn.location_kicker || 'Get In Touch',
          location_title: dataEn.location_title || 'Visit Us Today',
          location_description: dataEn.location_description || 'Located in the heart of Jakarta with easy access and a welcoming environment for families.',
          map_embed_url: cleanGoogleMapsEmbedUrl(dataEn.map_embed_url || '', dataEn.address),
          address: dataEn.address || 'Jl. Raya Permata Harapan No. 10, Jakarta Timur 13220',
          map_directions_url: dataEn.map_directions_url || 'https://maps.google.com/?q=Permata%20Harapan%20School%20Jakarta',
          phone: dataEn.phone || '+62 21 555 1234',
          email: dataEn.email || 'info@permataharapan.sch.id',
          office_hours_weekday: dataEn.office_hours_weekday || (Array.isArray(dataEn.office_hours) ? dataEn.office_hours[0] : 'Mon - Fri: 07:30 - 16:00'),
          office_hours_weekend: dataEn.office_hours_weekend || (Array.isArray(dataEn.office_hours) ? dataEn.office_hours[1] : 'Sat: 08:00 - 12:00'),
          social_instagram: dataEn.social_instagram || 'https://instagram.com/permataharapan',
          social_facebook: dataEn.social_facebook || 'https://facebook.com/permataharapan',
          social_youtube: dataEn.social_youtube || 'https://youtube.com/@permataharapan',
          social_linkedin: dataEn.social_linkedin || 'https://linkedin.com/school/permataharapan'
        };

        setFormData(newFormData);
        setOriginalData(newFormData);

        // Fetch pending changes
        if (foundSection.id) {
          try {
            const pendRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
              credentials: 'include',
              headers: getAuthHeaders()
            });
            if (pendRes.ok) {
              const pendJson = await pendRes.json();
              if (pendJson.success && Array.isArray(pendJson.data?.pending_changes)) {
                const pendMap = {};
                pendJson.data.pending_changes.forEach(change => {
                  if (change.status === 'pending') {
                    pendMap[change.field_id] = true;
                  }
                });
                setPendingFields(pendMap);
              }
            }
          } catch (err) {
            console.warn('Could not load pending changes for contact-info', err);
          }
        }
      }
    } catch (err) {
      console.error('Error loading contact-info section:', err);
      toast.error('Failed to load contact info data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMapEmbedChange = (val) => {
    if (typeof val === 'string' && (val.includes('<iframe') || val.includes('src='))) {
      const extracted = cleanGoogleMapsEmbedUrl(val, formData.address);
      if (extracted && extracted.startsWith('http')) {
        handleInputChange('map_embed_url', extracted);
        toast.info('Kode iframe Google Maps otomatis diekstrak menjadi tautan embed yang valid!');
        return;
      }
    }
    handleInputChange('map_embed_url', val);
  };

  const handleSubmitField = async (fieldId, proposedValue, currentVal, fieldDisplayName) => {
    if (!sectionId) {
      toast.error('Section not found in database. Please run migrations first.');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldId]: true }));
    try {
      let finalProposed = proposedValue !== undefined ? proposedValue : formData[fieldId];
      if (fieldId === 'map_embed_url') {
        finalProposed = cleanGoogleMapsEmbedUrl(finalProposed, formData.address);
      }

      const payload = {
        section_id: sectionId,
        field_id: fieldId,
        field_name: fieldDisplayName || (fieldId.replace(/_/g, ' ').toUpperCase()),
        old_value: currentVal !== undefined ? currentVal : originalData[fieldId],
        new_value: finalProposed,
        current_value: typeof currentVal === 'object' ? JSON.stringify(currentVal) : String(currentVal || ''),
        proposed_value: typeof finalProposed === 'object' ? JSON.stringify(finalProposed) : String(finalProposed || ''),
        locale: 'en'
      };

      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const json = await res.json();
      if (res.ok && (json.success || json.ok)) {
        toast.success(`Perubahan "${fieldDisplayName || fieldId}" berhasil diajukan untuk approval`);
        setPendingFields(prev => ({ ...prev, [fieldId]: true }));
        setOriginalData(prev => ({ ...prev, [fieldId]: proposedValue }));
        try {
          sessionStorage.removeItem('cms_page_contact_us');
          sessionStorage.removeItem('cms_sections_contact_us');
          window.dispatchEvent(new Event('cms-contact-us-updated'));
        } catch (e) {}
      } else {
        throw new Error(json.message || 'Submission failed');
      }
    } catch (err) {
      console.error('Submit field error:', err);
      toast.error(err.message || 'Failed to submit changes');
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Contact Info & Location...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />
      
      {/* Standard Unified Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <MapPin size={22} />
        </div>
        <div>
          <h2 className="unified-title">Contact Info & Campus Location</h2>
          <p className="unified-subtitle">
            Kelola nomor WhatsApp pesan pengunjung, teks formulir, alamat fisik kampus, Google Maps, dan tautan sosial media resmi.
          </p>
        </div>
        {sectionId && (
          <span className="unified-section-badge">ID: {sectionId}</span>
        )}
      </div>

      <div className="unified-form-body">
        {/* GROUP 1: WhatsApp & Inquiry Message */}
        <div className="unified-form-section-divider">
          <MessageSquare size={16} />
          <span>Formulir Pesan Pengunjung & WhatsApp</span>
        </div>

        <div className="unified-grid-2">
          {/* Form Kicker */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Form Kicker</label>
              {pendingFields.form_kicker && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.form_kicker}
                onChange={(e) => handleInputChange('form_kicker', e.target.value)}
                disabled={pendingFields.form_kicker}
                placeholder="e.g. Contact Form"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('form_kicker', formData.form_kicker, originalData.form_kicker, 'Form Kicker')}
                disabled={submitting.form_kicker || pendingFields.form_kicker || formData.form_kicker === originalData.form_kicker}
              >
                <Save size={14} /> {submitting.form_kicker ? 'Saving...' : (pendingFields.form_kicker ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>

          {/* Form Title */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Form Title</label>
              {pendingFields.form_title && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.form_title}
                onChange={(e) => handleInputChange('form_title', e.target.value)}
                disabled={pendingFields.form_title}
                placeholder="e.g. Send us a message"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('form_title', formData.form_title, originalData.form_title, 'Form Title')}
                disabled={submitting.form_title || pendingFields.form_title || formData.form_title === originalData.form_title}
              >
                <Save size={14} /> {submitting.form_title ? 'Saving...' : (pendingFields.form_title ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>
        </div>

        {/* WhatsApp Number */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Nomor WhatsApp Sekolah</label>
              <p className="unified-hint">Nomor tujuan penerima pesan pesan WhatsApp (contoh: 08815130338 atau 628815130338).</p>
            </div>
            {pendingFields.whatsapp_number && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              className="unified-input"
              value={formData.whatsapp_number}
              onChange={(e) => handleInputChange('whatsapp_number', e.target.value)}
              placeholder="08815130338"
              disabled={pendingFields.whatsapp_number}
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('whatsapp_number', formData.whatsapp_number, originalData.whatsapp_number, 'WhatsApp Number')}
              disabled={submitting.whatsapp_number || pendingFields.whatsapp_number || formData.whatsapp_number === originalData.whatsapp_number}
            >
              <Save size={14} /> {submitting.whatsapp_number ? 'Saving...' : (pendingFields.whatsapp_number ? 'Pending' : 'Save')}
            </button>
          </div>
        </div>

        {/* Form Description */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Form Description / Ringkasan</label>
              <p className="unified-hint">Petunjuk singkat yang tampil di atas form pesan pengunjung.</p>
            </div>
            {pendingFields.form_description && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>
          <div className="unified-input-action-column">
            <textarea
              className="unified-textarea"
              rows={2}
              value={formData.form_description}
              onChange={(e) => handleInputChange('form_description', e.target.value)}
              disabled={pendingFields.form_description}
              placeholder="Share your questions and our team will respond as soon as possible."
            />
            <div className="unified-align-right">
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('form_description', formData.form_description, originalData.form_description, 'Form Description')}
                disabled={submitting.form_description || pendingFields.form_description || formData.form_description === originalData.form_description}
              >
                <Save size={14} /> {submitting.form_description ? 'Saving...' : (pendingFields.form_description ? 'Pending' : 'Save Description')}
              </button>
            </div>
          </div>
        </div>

        {/* GROUP 2: Campus Location & Physical Address */}
        <div className="unified-form-section-divider">
          <MapPin size={16} />
          <span>Lokasi Fisik Kampus & Google Maps</span>
        </div>

        <div className="unified-grid-2">
          {/* Location Kicker */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Location Kicker</label>
              {pendingFields.location_kicker && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.location_kicker}
                onChange={(e) => handleInputChange('location_kicker', e.target.value)}
                disabled={pendingFields.location_kicker}
                placeholder="e.g. Get In Touch"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('location_kicker', formData.location_kicker, originalData.location_kicker, 'Location Kicker')}
                disabled={submitting.location_kicker || pendingFields.location_kicker || formData.location_kicker === originalData.location_kicker}
              >
                <Save size={14} /> {submitting.location_kicker ? 'Saving...' : (pendingFields.location_kicker ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>

          {/* Location Title */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Location Title</label>
              {pendingFields.location_title && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.location_title}
                onChange={(e) => handleInputChange('location_title', e.target.value)}
                disabled={pendingFields.location_title}
                placeholder="e.g. Visit Us Today"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('location_title', formData.location_title, originalData.location_title, 'Location Title')}
                disabled={submitting.location_title || pendingFields.location_title || formData.location_title === originalData.location_title}
              >
                <Save size={14} /> {submitting.location_title ? 'Saving...' : (pendingFields.location_title ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>
        </div>

        {/* Physical Address */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Alamat Lengkap Kampus</label>
              <p className="unified-hint">Alamat fisik yang ditampilkan di kartu lokasi.</p>
            </div>
            {pendingFields.address && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              className="unified-input"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              disabled={pendingFields.address}
              placeholder="Jl. Raya Permata Harapan No. 10..."
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('address', formData.address, originalData.address, 'Campus Address')}
              disabled={submitting.address || pendingFields.address || formData.address === originalData.address}
            >
              <Save size={14} /> {submitting.address ? 'Saving...' : (pendingFields.address ? 'Pending' : 'Save')}
            </button>
          </div>
        </div>

        {/* Maps Links Grid */}
        <div className="unified-grid-2">
          <div className="unified-field-card">
            <div className="unified-field-header">
              <div>
                <label className="unified-label">Google Maps Embed URL (Iframe)</label>
                <p className="unified-hint">URL embed peta interaktif untuk iframe.</p>
              </div>
              {pendingFields.map_embed_url && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.map_embed_url}
                onChange={(e) => handleMapEmbedChange(e.target.value)}
                onPaste={(e) => {
                  const pasteText = e.clipboardData?.getData('text') || '';
                  if (pasteText.includes('<iframe') || pasteText.includes('src=')) {
                    e.preventDefault();
                    handleMapEmbedChange(pasteText);
                  }
                }}
                disabled={pendingFields.map_embed_url}
                placeholder="https://www.google.com/maps?q=...&output=embed atau tempel tag <iframe>"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('map_embed_url', formData.map_embed_url, originalData.map_embed_url, 'Map Embed URL')}
                disabled={submitting.map_embed_url || pendingFields.map_embed_url || formData.map_embed_url === originalData.map_embed_url}
              >
                <Save size={14} /> {submitting.map_embed_url ? 'Saving...' : (pendingFields.map_embed_url ? 'Pending' : 'Save')}
              </button>
            </div>
            <p className="unified-hint" style={{ marginTop: '6px', fontSize: '11.5px', color: '#64748b' }}>
              Tip: Anda dapat langsung menempelkan (paste) seluruh kode <code>&lt;iframe&gt;</code> dari Google Maps (Bagikan &gt; Sematkan peta). Sistem akan otomatis mengekstrak URL embed yang valid.
            </p>
            {formData.map_embed_url && (
              <div style={{ marginTop: '10px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#475569', padding: '6px 10px', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Pratinjau Peta Embed:</span>
                  <span style={{ color: '#16a34a', fontSize: '11px', fontWeight: 600 }}>Tautan Siap Tampil</span>
                </div>
                <iframe
                  title="Pratinjau Google Maps"
                  src={cleanGoogleMapsEmbedUrl(formData.map_embed_url, formData.address)}
                  style={{ width: '100%', height: '160px', border: 0, display: 'block' }}
                  loading="lazy"
                />
              </div>
            )}
          </div>

          <div className="unified-field-card">
            <div className="unified-field-header">
              <div>
                <label className="unified-label">Directions Link (Direct Link)</label>
                <p className="unified-hint">Tautan petunjuk arah ke Google Maps aplikasi/browser.</p>
              </div>
              {pendingFields.map_directions_url && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.map_directions_url}
                onChange={(e) => handleInputChange('map_directions_url', e.target.value)}
                disabled={pendingFields.map_directions_url}
                placeholder="https://maps.google.com/?q=..."
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('map_directions_url', formData.map_directions_url, originalData.map_directions_url, 'Map Directions URL')}
                disabled={submitting.map_directions_url || pendingFields.map_directions_url || formData.map_directions_url === originalData.map_directions_url}
              >
                <Save size={14} /> {submitting.map_directions_url ? 'Saving...' : (pendingFields.map_directions_url ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>
        </div>

        {/* Location Description */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Location Description</label>
              <p className="unified-hint">Deskripsi aksesibilitas dan kemudahan rute menuju kampus.</p>
            </div>
            {pendingFields.location_description && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>
          <div className="unified-input-action-column">
            <textarea
              className="unified-textarea"
              rows={2}
              value={formData.location_description}
              onChange={(e) => handleInputChange('location_description', e.target.value)}
              disabled={pendingFields.location_description}
              placeholder="Located in the heart of Jakarta..."
            />
            <div className="unified-align-right">
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('location_description', formData.location_description, originalData.location_description, 'Location Description')}
                disabled={submitting.location_description || pendingFields.location_description || formData.location_description === originalData.location_description}
              >
                <Save size={14} /> {submitting.location_description ? 'Saving...' : (pendingFields.location_description ? 'Pending' : 'Save Description')}
              </button>
            </div>
          </div>
        </div>

        {/* GROUP 3: Contact Channels & Office Hours */}
        <div className="unified-form-section-divider">
          <Phone size={16} />
          <span>Telepon, Email & Jam Operasional Kantor</span>
        </div>

        <div className="unified-grid-2">
          {/* Telephone */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Nomor Telepon Kantor</label>
              {pendingFields.phone && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                disabled={pendingFields.phone}
                placeholder="+62 21 555 1234"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('phone', formData.phone, originalData.phone, 'Phone Number')}
                disabled={submitting.phone || pendingFields.phone || formData.phone === originalData.phone}
              >
                <Save size={14} /> {submitting.phone ? 'Saving...' : (pendingFields.phone ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>

          {/* Email */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Alamat Email Resmi Sekolah</label>
              {pendingFields.email && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="email"
                className="unified-input"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={pendingFields.email}
                placeholder="info@permataharapan.sch.id"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('email', formData.email, originalData.email, 'Email Address')}
                disabled={submitting.email || pendingFields.email || formData.email === originalData.email}
              >
                <Save size={14} /> {submitting.email ? 'Saving...' : (pendingFields.email ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>
        </div>

        {/* Office Hours */}
        <div className="unified-grid-2">
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Jam Operasional Weekday (Senin - Jumat)</label>
              {pendingFields.office_hours_weekday && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.office_hours_weekday}
                onChange={(e) => handleInputChange('office_hours_weekday', e.target.value)}
                placeholder="Mon - Fri: 07:30 - 16:00"
                disabled={pendingFields.office_hours_weekday}
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('office_hours_weekday', formData.office_hours_weekday, originalData.office_hours_weekday, 'Weekday Office Hours')}
                disabled={submitting.office_hours_weekday || pendingFields.office_hours_weekday || formData.office_hours_weekday === originalData.office_hours_weekday}
              >
                <Save size={14} /> {submitting.office_hours_weekday ? 'Saving...' : (pendingFields.office_hours_weekday ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>

          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Jam Operasional Weekend (Sabtu)</label>
              {pendingFields.office_hours_weekend && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.office_hours_weekend}
                onChange={(e) => handleInputChange('office_hours_weekend', e.target.value)}
                placeholder="Sat: 08:00 - 12:00"
                disabled={pendingFields.office_hours_weekend}
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('office_hours_weekend', formData.office_hours_weekend, originalData.office_hours_weekend, 'Weekend Office Hours')}
                disabled={submitting.office_hours_weekend || pendingFields.office_hours_weekend || formData.office_hours_weekend === originalData.office_hours_weekend}
              >
                <Save size={14} /> {submitting.office_hours_weekend ? 'Saving...' : (pendingFields.office_hours_weekend ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>
        </div>

        {/* GROUP 4: Social Media Links */}
        <div className="unified-form-section-divider">
          <Globe size={16} />
          <span>Kanal Media Sosial Resmi</span>
        </div>

        <div className="unified-grid-2">
          {/* Instagram */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Instagram URL</label>
              {pendingFields.social_instagram && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="url"
                className="unified-input"
                value={formData.social_instagram}
                onChange={(e) => handleInputChange('social_instagram', e.target.value)}
                disabled={pendingFields.social_instagram}
                placeholder="https://instagram.com/permataharapan"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('social_instagram', formData.social_instagram, originalData.social_instagram, 'Instagram URL')}
                disabled={submitting.social_instagram || pendingFields.social_instagram || formData.social_instagram === originalData.social_instagram}
              >
                <Save size={14} /> {submitting.social_instagram ? 'Saving...' : (pendingFields.social_instagram ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>

          {/* Facebook */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Facebook URL</label>
              {pendingFields.social_facebook && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="url"
                className="unified-input"
                value={formData.social_facebook}
                onChange={(e) => handleInputChange('social_facebook', e.target.value)}
                disabled={pendingFields.social_facebook}
                placeholder="https://facebook.com/permataharapan"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('social_facebook', formData.social_facebook, originalData.social_facebook, 'Facebook URL')}
                disabled={submitting.social_facebook || pendingFields.social_facebook || formData.social_facebook === originalData.social_facebook}
              >
                <Save size={14} /> {submitting.social_facebook ? 'Saving...' : (pendingFields.social_facebook ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>

          {/* YouTube */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">YouTube URL</label>
              {pendingFields.social_youtube && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="url"
                className="unified-input"
                value={formData.social_youtube}
                onChange={(e) => handleInputChange('social_youtube', e.target.value)}
                disabled={pendingFields.social_youtube}
                placeholder="https://youtube.com/@permataharapan"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('social_youtube', formData.social_youtube, originalData.social_youtube, 'YouTube URL')}
                disabled={submitting.social_youtube || pendingFields.social_youtube || formData.social_youtube === originalData.social_youtube}
              >
                <Save size={14} /> {submitting.social_youtube ? 'Saving...' : (pendingFields.social_youtube ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>

          {/* LinkedIn */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">LinkedIn URL</label>
              {pendingFields.social_linkedin && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="url"
                className="unified-input"
                value={formData.social_linkedin}
                onChange={(e) => handleInputChange('social_linkedin', e.target.value)}
                disabled={pendingFields.social_linkedin}
                placeholder="https://linkedin.com/school/permataharapan"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('social_linkedin', formData.social_linkedin, originalData.social_linkedin, 'LinkedIn URL')}
                disabled={submitting.social_linkedin || pendingFields.social_linkedin || formData.social_linkedin === originalData.social_linkedin}
              >
                <Save size={14} /> {submitting.social_linkedin ? 'Saving...' : (pendingFields.social_linkedin ? 'Pending' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
